import React, { useState, useMemo, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { LEVERAGE_MIN, LEVERAGE_MAX } from "./constants";
import { formatCurrency } from "../../sdk/utils/display";

/**
 * Leverage Selection Modal
 */
export function LeverageModal({
  isOpen,
  currentLeverage,
  tempLeverage,
  maxRatio,
  onLeverageChange,
  onConfirm,
  onCancel,
}: {
  isOpen: boolean;
  currentLeverage: number;
  tempLeverage: number;
  maxRatio: string;
  onLeverageChange: (leverage: number) => void;
  onConfirm: () => void;
  onCancel: () => void;
}): JSX.Element | null {
  if (!isOpen) return null;

  const modalContent = (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Select Leverage</h3>
        </div>
        <div className="modal-body">
          {/* Maximum Ratio Display */}
          <div className="leverage-max-ratio">
            <div className="leverage-max-ratio-label">Maximum Ratio</div>
            <div className="leverage-max-ratio-value">{maxRatio}x</div>
          </div>

          {/* Leverage Slider */}
          <div className="leverage-slider-container">
            <input
              type="range"
              min={LEVERAGE_MIN}
              max={LEVERAGE_MAX}
              value={tempLeverage}
              onChange={(e) => onLeverageChange(parseInt(e.target.value))}
              className="leverage-slider"
            />
            <div className="leverage-slider-labels">
              <span>1x</span>
              <span className="leverage-current-value">{tempLeverage}x</span>
              <span>100x</span>
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button onClick={onCancel} className="btn btn-secondary">
            Cancel
          </button>
          <button onClick={onConfirm} className="btn btn-primary">
            Confirm
          </button>
        </div>
      </div>
    </div>
  );

  // Use portal to render modal at document body level
  return typeof document !== "undefined"
    ? createPortal(modalContent, document.body)
    : null;
}

/**
 * Transfer Modal
 */
export function TransferModal({
  isOpen,
  fromValue,
  toValue,
  options,
  bucketsData,
  transferAmount,
  createBucketAmount,
  onFromChange,
  onToChange,
  onTransferAmountChange,
  onCreateBucketAmountChange,
  onConfirm,
  onCancel,
  spotWalletBalance = 0,
  activeTab,
  onActiveTabChange,
  isTransferring = false,
  onOpen,
}: {
  isOpen: boolean;
  fromValue: string;
  toValue: string;
  options: Array<{ value: string; label: string }>;
  bucketsData: Array<{ bucketId: string; nominalValue: number }>;
  transferAmount: number;
  createBucketAmount: number;
  onFromChange: (value: string) => void;
  onToChange: (value: string) => void;
  onTransferAmountChange: (amount: number) => void;
  onCreateBucketAmountChange: (amount: number) => void;
  onConfirm: () => void;
  onCancel: () => void;
  spotWalletBalance?: number;
  activeTab?: "transfer" | "create";
  onActiveTabChange?: (tab: "transfer" | "create") => void;
  isTransferring?: boolean;
  onOpen?: () => void;
}): JSX.Element | null {
  // Use internal state if not provided from parent (for backward compatibility)
  const [internalActiveTab, setInternalActiveTab] = useState<"transfer" | "create">("transfer");
  const currentActiveTab = activeTab ?? internalActiveTab;
  const handleActiveTabChange = onActiveTabChange ?? setInternalActiveTab;
  
  // Track previous isOpen state to detect when modal opens
  const prevIsOpenRef = useRef<boolean>(false);
  
  // Refresh spot wallet balance when modal opens
  useEffect(() => {
    if (isOpen && !prevIsOpenRef.current && onOpen) {
      // Modal just opened - refresh the data
      onOpen();
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen, onOpen]);

  // Get transferable funds for selected bucket or spot wallet
  const selectedBucket = useMemo(() => {
    if (fromValue === "spot-wallet") {
      return null; // Spot wallet is not in bucketsData
    }
    return bucketsData.find((b) => b.bucketId === fromValue);
  }, [bucketsData, fromValue]);

  // Calculate max transferable - use spot wallet balance if selected
  const maxTransferable = useMemo(() => {
    if (fromValue === "spot-wallet") {
      return spotWalletBalance;
    }
    return selectedBucket?.nominalValue || 0;
  }, [fromValue, spotWalletBalance, selectedBucket]);
  const minAmount = 10;
  const maxAmount = Math.max(minAmount, maxTransferable);

  // Reset amounts when bucket changes
  React.useEffect(() => {
    if (fromValue && maxAmount > 0) {
      const currentAmount = currentActiveTab === "transfer" ? transferAmount : createBucketAmount;
      if (currentAmount > maxAmount) {
        if (currentActiveTab === "transfer") {
          onTransferAmountChange(maxAmount);
        } else {
          onCreateBucketAmountChange(maxAmount);
        }
      } else if (currentAmount < minAmount && maxAmount >= minAmount) {
        if (currentActiveTab === "transfer") {
          onTransferAmountChange(minAmount);
        } else {
          onCreateBucketAmountChange(minAmount);
        }
      }
    }
  }, [fromValue, maxAmount, currentActiveTab, transferAmount, createBucketAmount, onTransferAmountChange, onCreateBucketAmountChange]);

  if (!isOpen) return null;

  const isTransferValid = fromValue && toValue && transferAmount >= minAmount && transferAmount <= maxAmount;
  const isCreateValid = fromValue && createBucketAmount >= minAmount && createBucketAmount <= maxAmount;

  const modalContent = (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal-content transfer-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Transfer Funds</h3>
        </div>

        {/* Tabs */}
        <div className="transfer-modal-tabs">
          <button
            className={`transfer-tab ${currentActiveTab === "transfer" ? "active" : ""}`}
            onClick={() => handleActiveTabChange("transfer")}
            type="button"
            disabled={isTransferring}
          >
            Transfer
          </button>
          <button
            className={`transfer-tab ${currentActiveTab === "create" ? "active" : ""}`}
            onClick={() => handleActiveTabChange("create")}
            type="button"
            disabled={isTransferring}
          >
            Create Bucket
          </button>
        </div>

        <div className="modal-body">
          {currentActiveTab === "transfer" ? (
            <>
              {/* From Selection Field */}
              <div className="transfer-field-group">
                <label className="transfer-field-label">From</label>
                <select
                  value={fromValue}
                  onChange={(e) => onFromChange(e.target.value)}
                  className="transfer-select"
                >
                  <option value="">Select source bucket</option>
                  {options.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* To Selection Field */}
              <div className="transfer-field-group">
                <label className="transfer-field-label">To</label>
                <select
                  value={toValue}
                  onChange={(e) => onToChange(e.target.value)}
                  className="transfer-select"
                  disabled={!fromValue}
                >
                  <option value="">Select destination bucket</option>
                  {options
                    .filter((option) => option.value !== fromValue)
                    .map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                </select>
              </div>

              {/* Amount Range Input */}
              {fromValue && maxAmount >= minAmount ? (
                <div className="transfer-field-group">
                  <div className="transfer-amount-header">
                    <label className="transfer-field-label">Amount</label>
                    <div className="transfer-available-balance">
                      Available: {formatCurrency(maxTransferable)}
                    </div>
                  </div>
                  <div className="transfer-amount-display">
                    <span className="transfer-amount-value">
                      {formatCurrency(transferAmount)}
                    </span>
                  </div>
                  <div className="transfer-amount-container">
                    <input
                      type="range"
                      min={minAmount}
                      max={maxAmount}
                      step={0.01}
                      value={transferAmount}
                      onChange={(e) =>
                        onTransferAmountChange(parseFloat(e.target.value))
                      }
                      className="transfer-amount-slider"
                      style={{
                        "--slider-progress": `${((transferAmount - minAmount) / (maxAmount - minAmount)) * 100}%`,
                      } as React.CSSProperties}
                    />
                    <div className="transfer-amount-labels">
                      <span>{formatCurrency(minAmount)}</span>
                      <span>{formatCurrency(maxAmount)}</span>
                    </div>
                  </div>
                </div>
              ) : fromValue ? (
                <div className="transfer-field-group">
                  <div className="transfer-insufficient-funds">
                    Insufficient funds. Minimum required: {formatCurrency(minAmount)}
                  </div>
                </div>
              ) : null}
            </>
          ) : (
            <>
              {/* From Selection Field for Create Bucket */}
              <div className="transfer-field-group">
                <label className="transfer-field-label">From</label>
                <select
                  value={fromValue}
                  onChange={(e) => onFromChange(e.target.value)}
                  className="transfer-select"
                >
                  <option value="">Select source bucket</option>
                  {options.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Amount Range Input for Create Bucket */}
              {fromValue && maxAmount >= minAmount ? (
                <div className="transfer-field-group">
                  <div className="transfer-amount-header">
                    <label className="transfer-field-label">Amount</label>
                    <div className="transfer-available-balance">
                      Available: {formatCurrency(maxTransferable)}
                    </div>
                  </div>
                  <div className="transfer-amount-display">
                    <span className="transfer-amount-value">
                      {formatCurrency(createBucketAmount)}
                    </span>
                  </div>
                  <div className="transfer-amount-container">
                    <input
                      type="range"
                      min={minAmount}
                      max={maxAmount}
                      step={0.01}
                      value={createBucketAmount}
                      onChange={(e) =>
                        onCreateBucketAmountChange(parseFloat(e.target.value))
                      }
                      className="transfer-amount-slider"
                      style={{
                        "--slider-progress": `${((createBucketAmount - minAmount) / (maxAmount - minAmount)) * 100}%`,
                      } as React.CSSProperties}
                    />
                    <div className="transfer-amount-labels">
                      <span>{formatCurrency(minAmount)}</span>
                      <span>{formatCurrency(maxAmount)}</span>
                    </div>
                  </div>
                </div>
              ) : fromValue ? (
                <div className="transfer-field-group">
                  <div className="transfer-insufficient-funds">
                    Insufficient funds. Minimum required: {formatCurrency(minAmount)}
                  </div>
                </div>
              ) : null}
            </>
          )}
        </div>

        <div className="modal-footer">
          <button 
            onClick={onCancel} 
            className="btn btn-secondary"
            disabled={isTransferring}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="btn btn-primary"
            disabled={
              isTransferring || 
              (currentActiveTab === "transfer" ? !isTransferValid : !isCreateValid)
            }
          >
            {isTransferring ? "Processing..." : "Confirm"}
          </button>
        </div>
      </div>
    </div>
  );

  // Use portal to render modal at document body level
  return typeof document !== "undefined"
    ? createPortal(modalContent, document.body)
    : null;
}
